/*
  <menu-drawer> — mobile navigation drawer, ported 1:1 from the Concept theme
  (its ModalElement + MenuDrawer + MenuDetails classes). The swipe-to-dismiss
  half lives in assets/gesture-element.js, shared with the cart drawer.

  Shape:
    < 640px   a bottom sheet — slides up, rounded top corners, grab handle,
              swipe down to dismiss.
    >= 640px  a left slide-in panel (max-width 36rem) with a close button.

  Behaviour:
    - opens from any [aria-controls="MenuDrawer"] control (hamburger, overlay,
      close button); Escape closes.
    - top-level items stagger in (translateX(-20px) -> 0, opacity 0 -> 1,
      600ms, 100ms apart) starting 300ms after the sheet begins to travel.
    - items with children push a full-height submenu in from the right while
      the parent list slides 30% to the left; a back button pushes it away.
      `accordion` mode swaps that for an expanding height accordion.
    - the sheet is appended to <body> while open, so it escapes the header's
      stacking context, and is put back on close.

  Concept drives the submenus with `is="menu-details"` customized built-ins,
  which WebKit does not implement — on a *mobile* menu that is fatal. The same
  DOM and the same classes are driven here by delegated clicks instead, so the
  CSS (and therefore the animation) is untouched.
*/
(function () {
  if (customElements.get('menu-drawer')) return;

  var EASE_STAGGER = 'cubic-bezier(0.075, 0.82, 0.165, 1)';
  var FOCUSABLE =
    'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Resolves once `el`'s transition ends, or after `fallback` ms if the browser
     never fires it (interrupted transition, display:none, reduced motion). */
  function afterTransition(el, fallback) {
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      var onEnd = function (event) {
        if (event.target === el) finish();
      };
      el.addEventListener('transitionend', onEnd);
      setTimeout(finish, fallback);
    });
  }

  /* ------------------------------------------------------------------ *
   * <menu-drawer>
   * ------------------------------------------------------------------ */
  customElements.define(
    'menu-drawer',
    class MenuDrawer extends HTMLElement {
      static get observedAttributes() {
        return ['open'];
      }

      get open() {
        return this.hasAttribute('open');
      }

      get accordion() {
        return this.hasAttribute('data-accordion');
      }

      get controls() {
        return Array.from(document.querySelectorAll('[aria-controls="' + this.id + '"]'));
      }

      get overlay() {
        return this.querySelector('[data-drawer-overlay]');
      }

      /* The sheet. Show/hide are timed off this, not the overlay: the backdrop
         fades in 400ms while the sheet is still travelling for 600ms, and
         hiding on the overlay's cue would snap the sheet away mid-slide. */
      get panel() {
        return this.querySelector('.drawer__inner');
      }

      /* Only the resting top-level items stagger in — never a list that is
         currently pushed aside by an open submenu. */
      get menuItems() {
        return Array.from(this.querySelectorAll('.drawer__menu:not(.active) > li'));
      }

      connectedCallback() {
        // Re-parenting to <body> on open disconnects and reconnects us; the
        // listeners survive that and must not be bound twice.
        if (this.bound || this.reparenting) return;
        this.bound = true;

        // A section reload in the theme editor can strand an open drawer on
        // <body> — two #MenuDrawer nodes would then fight over the controls.
        document.querySelectorAll('body > #' + this.id).forEach(function (stale) {
          if (stale !== this) stale.remove();
        }, this);

        this.abortController = new AbortController();
        var signal = this.abortController.signal;
        var self = this;

        this.controls.forEach(function (button) {
          button.addEventListener('click', self.onControlClick.bind(self), { signal: signal });
        });
        document.addEventListener(
          'keyup',
          function (event) {
            if (event.code === 'Escape') self.hide();
          },
          { signal: signal }
        );
        this.addEventListener('click', this.onDrawerClick.bind(this), { signal: signal });
        this.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });

        this.gesture = this.querySelector('gesture-element');
        if (this.gesture && this.gesture.enableDismiss) {
          this.gesture.enableDismiss({
            wrap: this.panel,
            overlay: this.overlay,
            isOpen: function () { return self.open; },
            onDismiss: function () { self.hide(); }
          });
        }

        this.setupCursor();
      }

      /* ---- Follow cursor over the backdrop (same as the cart drawer) ----
         A circular × eases toward the pointer, grows over the overlay
         (.is-active) and scales up on press (.is-pressed). The easing
         runs in a rAF loop rather than inside mousemove, so the circle catches
         up even when the pointer stops — otherwise it is stranded mid-lag at
         the moment you click to close. */
      setupCursor() {
        this.cursorEl = this.querySelector('[data-drawer-cursor]');
        if (!this.cursorEl) return;
        // Hover-capable, fine pointers only — matches the `cursor: none` guard.
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        this.cursorEnabled = true;
        this.cursorTarget = { x: 0, y: 0 };
        this.cursorPos = { x: 0, y: 0 };
        this.cursorSeeded = false;
        this.cursorRAF = null;

        var self = this;
        document.addEventListener('mousemove', function (event) {
          self.cursorTarget.x = event.clientX;
          self.cursorTarget.y = event.clientY;
          if (!self.cursorSeeded) {
            // First sighting: drop the circle straight onto the pointer.
            self.cursorPos.x = event.clientX;
            self.cursorPos.y = event.clientY;
            self.cursorSeeded = true;
          }
        });

        var cursor = this.cursorEl;
        var overlay = this.overlay;
        var activate = function () {
          cursor.classList.add('is-active');
          document.documentElement.classList.add('ink-hidden');
        };
        overlay.addEventListener('mouseenter', activate);
        overlay.addEventListener('mousemove', activate);
        overlay.addEventListener('mouseleave', function () {
          cursor.classList.remove('is-active', 'is-pressed');
          document.documentElement.classList.remove('ink-hidden');
        });
        overlay.addEventListener('mousedown', function () { cursor.classList.add('is-pressed'); });
        overlay.addEventListener('mouseup', function () { cursor.classList.remove('is-pressed'); });
      }

      /* rAF easing loop — runs only while the drawer is open. */
      startCursorLoop() {
        if (!this.cursorEnabled || this.cursorRAF) return;
        var self = this;
        var step = function () {
          // Ease ~20% per frame toward the live pointer.
          self.cursorPos.x += (self.cursorTarget.x - self.cursorPos.x) * 0.2;
          self.cursorPos.y += (self.cursorTarget.y - self.cursorPos.y) * 0.2;
          self.cursorEl.style.setProperty('--cursor-x', self.cursorPos.x + 'px');
          self.cursorEl.style.setProperty('--cursor-y', self.cursorPos.y + 'px');
          self.cursorRAF = requestAnimationFrame(step);
        };
        this.cursorRAF = requestAnimationFrame(step);
      }

      stopCursorLoop() {
        if (!this.cursorRAF) return;
        cancelAnimationFrame(this.cursorRAF);
        this.cursorRAF = null;
      }

      disconnectedCallback() {
        // The drawer re-parents itself to <body> on open; that disconnect must
        // not tear the listeners down.
        if (this.reparenting) return;
        this.bound = false;
        if (this.abortController) this.abortController.abort();
        document.documentElement.classList.remove('menu-drawer-locked');
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name !== 'open') return;

        this.controls.forEach(function (button) {
          button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true');
        });

        if (oldValue === null && newValue === '') {
          this.hidden = false;
          this.removeAttribute('inert');

          if (this.parentElement !== document.body) {
            this.originalParent = this.parentElement;
            this.reparenting = true;
            document.body.append(this);
            this.reparenting = false;
          }

          // Commit the off-screen start transform before flipping [active];
          // without this the sheet can jump straight to its resting place.
          void this.offsetWidth; // eslint-disable-line no-unused-expressions

          setTimeout(
            function () {
              this.setAttribute('active', '');
            }.bind(this),
            75
          );

          afterTransition(this.panel, 1200).then(this.afterShow.bind(this));
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');
          this.removeAttribute('active');

          afterTransition(this.panel, 1200).then(this.afterHide.bind(this));
        }
      }

      /* ---- open / close ---- */
      onControlClick(event) {
        event.preventDefault();
        this.open ? this.hide() : this.show(event.currentTarget);
      }

      show(activeElement) {
        if (this.open) return;
        this.activeElement = activeElement || document.activeElement;
        this.setAttribute('open', '');
        this.beforeShow(); // after [open], so the items have layout to animate
        document.documentElement.classList.add('menu-drawer-locked');
        this.startCursorLoop();
        if (window.lenis) window.lenis.stop();
      }

      hide() {
        if (!this.open) return;
        this.beforeHide();
        if (this.gesture && this.gesture.resetDismiss) this.gesture.resetDismiss();
        this.removeAttribute('open');
        document.documentElement.classList.remove('menu-drawer-locked');
        if (this.cursorEl) this.cursorEl.classList.remove('is-active', 'is-pressed');
        document.documentElement.classList.remove('ink-hidden');
        if (window.lenis) window.lenis.start();
      }

      beforeShow() {
        if (reduceMotion()) return;

        // The 300ms lead-in lets the sheet get moving before the items arrive.
        // `fill: backwards` holds each item at its start frame for the whole
        // delay, so nothing flashes at full opacity first.
        this.menuItems.forEach(function (item, index) {
          item.animate(
            [
              { transform: 'translateX(-20px)', opacity: 0 },
              { transform: 'translateX(0)', opacity: 1 }
            ],
            { duration: 600, delay: 300 + index * 100, easing: EASE_STAGGER, fill: 'backwards' }
          );
        });
      }

      beforeHide() {
        this.menuItems.forEach(function (item) {
          item.getAnimations().forEach(function (animation) {
            animation.cancel();
          });
        });

        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(this.closeSubmenus.bind(this), 300);
      }

      afterShow() {
        // The header, not the first link — a focus ring on "Parduotuvė" the
        // instant the sheet lands reads as a bug.
        this.trapFocus(this, this.gesture);
      }

      afterHide() {
        if (!this.hasAttribute('inert')) return; // re-opened mid-transition

        // Skip the trip home if the header was re-rendered underneath us —
        // that parent is detached, and going back to it would delete the drawer.
        if (this.parentElement === document.body && this.originalParent && this.originalParent.isConnected) {
          this.reparenting = true;
          this.originalParent.append(this);
          this.reparenting = false;
        }
        this.originalParent = null;
        this.hidden = true;
        this.stopCursorLoop();
        if (this.activeElement && this.activeElement.focus) this.activeElement.focus({ preventScroll: true });
      }

      /* ---- submenus (Concept's MenuDetails / AccordionDetails) ---- */
      onDrawerClick(event) {
        if (event.target.closest('[data-drawer-overlay]')) {
          this.hide();
          return;
        }

        var back = event.target.closest('[data-close]');
        if (back && this.contains(back)) {
          this.closeSubmenu(back.closest('details'));
          return;
        }

        var summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;

        event.preventDefault();
        var details = summary.closest('details');
        if (this.accordion) {
          this.toggleAccordion(details);
        } else if (!details.classList.contains('active')) {
          this.openSubmenu(details);
        }
      }

      openSubmenu(details) {
        var self = this;
        var summary = details.querySelector('summary');
        details.setAttribute('open', '');

        // One frame of [open] before .active, so `transition: inset` has a
        // laid-out start position to travel from.
        setTimeout(function () {
          var parent = details.closest('[data-parent]');
          if (parent) parent.classList.add('active');
          details.classList.add('active');
          summary.setAttribute('aria-expanded', 'true');
          summary.setAttribute('tabindex', '-1');

          setTimeout(function () {
            self.trapFocus(details, details.querySelector('[data-close]'));
          }, 100);
        }, 100);
      }

      closeSubmenu(details) {
        if (!details) return;
        var parent = details.closest('[data-parent]');
        var summary = details.querySelector('summary');

        if (parent) parent.classList.remove('active');
        details.classList.remove('active');
        summary.setAttribute('aria-expanded', 'false');
        summary.removeAttribute('tabindex');

        // Keep [open] until the slide-out finishes, or the panel vanishes
        // mid-travel.
        setTimeout(function () {
          details.removeAttribute('open');
        }, 400);

        this.trapFocus(this, summary);
      }

      /* Deepest first, so each parent list is still in the DOM when its child
         hands focus back. */
      closeSubmenus() {
        var open = Array.from(this.querySelectorAll('details[open]')).reverse();
        open.forEach(function (details) {
          var parent = details.closest('[data-parent]');
          if (parent) parent.classList.remove('active');
          details.classList.remove('active');
          details.removeAttribute('open');
          var summary = details.querySelector('summary');
          summary.setAttribute('aria-expanded', 'false');
          summary.removeAttribute('tabindex');
          details.style.height = '';
          details.style.overflow = '';
        });
      }

      toggleAccordion(details) {
        var summary = details.querySelector('summary');
        var content = summary.nextElementSibling;
        var opening = !details.hasAttribute('open');

        summary.setAttribute('aria-expanded', String(opening));

        if (reduceMotion()) {
          details.toggleAttribute('open', opening);
          return;
        }

        details.getAnimations().forEach(function (animation) {
          animation.cancel();
        });
        details.style.overflow = 'hidden';

        var settle = function () {
          details.style.height = 'auto';
          details.style.overflow = '';
        };

        if (opening) {
          details.setAttribute('open', '');
          details
            .animate([{ height: summary.clientHeight + 'px' }, { height: details.scrollHeight + 'px' }], {
              duration: 250,
              easing: 'ease'
            })
            .finished.then(settle)
            .catch(function () {});
          content.animate(
            [
              { opacity: 0, transform: 'translateY(10px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ],
            { duration: 150, delay: 150, easing: 'ease', fill: 'backwards' }
          );
        } else {
          content.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease' });
          details
            .animate([{ height: details.clientHeight + 'px' }, { height: summary.clientHeight + 'px' }], {
              duration: 250,
              easing: 'ease'
            })
            .finished.then(function () {
              details.removeAttribute('open');
              settle();
            })
            .catch(function () {});
        }
      }

      /* ---- focus ---- */
      onKeydown(event) {
        if (event.key !== 'Tab' || !this.trapRoot) return;

        var focusable = Array.from(this.trapRoot.querySelectorAll(FOCUSABLE)).filter(function (el) {
          return el.offsetParent !== null && el.getAttribute('tabindex') !== '-1';
        });
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      trapFocus(root, focusElement) {
        this.trapRoot = root || this;
        var target = focusElement || this.trapRoot.querySelector(FOCUSABLE);
        if (target) target.focus({ preventScroll: true });
      }
    }
  );
})();
